import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const LandingPage = () => {
  const [activeDemo, setActiveDemo] = useState('ai-diagnosis');
  const navigate = useNavigate();

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Features array for free tier
  const features = [
    {
      icon: 'Brain',
      title: 'AI Plant Diagnosis',
      description: 'Upload photos for instant pest and disease identification using advanced computer vision.',
      onClick: () => navigate('/ai-chat-assistant')
    },
    {
      icon: 'Calculator',
      title: 'Basic Calculators',
      description: 'Simple mixing ratios and application rate calculations for common treatments.',
      onClick: () => navigate('/premium-calculator-suite')
    },
    {
      icon: 'Database',
      title: 'Product Browser',
      description: 'Search our database of IPM products with basic product information and usage guidelines.',
      onClick: () => navigate('/product-browser')
    },
    {
      icon: 'BookOpen',
      title: 'Learning Center',
      description: 'Access educational content about pest identification and basic IPM principles.',
      onClick: () => navigate('/ipm-learning-center')
    },
    {
      icon: 'MessageSquare',
      title: 'AI Chat Assistant',
      description: 'Get basic IPM advice and product recommendations from our AI assistant.',
      onClick: () => navigate('/ai-chat-assistant')
    },
    {
      icon: 'Smartphone',
      title: 'Mobile Optimized',
      description: 'Full functionality on mobile devices with responsive design and touch-friendly interface.',
      onClick: () => navigate('/ipm-operations-overview')
    }
  ];

  // Premium features array
  const premiumFeatures = [
    {
      icon: 'Calculator',
      title: 'Advanced Calculators',
      description: 'Precision beneficial release rates, chemical mixing, and IPM plan generation tools.',
      onClick: () => navigate('/premium-calculator-suite')
    },
    {
      icon: 'BarChart3',
      title: 'Treatment Analytics',
      description: 'Comprehensive effectiveness tracking with ROI analysis and predictive insights.',
      onClick: () => navigate('/treatment-analytics')
    },
    {
      icon: 'Calendar',
      title: 'Treatment Planner',
      description: 'Automated IPM scheduling with calendar integration and task management.',
      onClick: () => navigate('/task-management')
    },
    {
      icon: 'Shield',
      title: 'Compliance Dashboard',
      description: 'Automated compliance tracking with regulatory reporting and audit trails.',
      onClick: () => navigate('/compliance-dashboard')
    },
    {
      icon: 'Brain',
      title: 'Advanced AI Features',
      description: 'Priority AI processing, detailed confidence scoring, and expert-level recommendations.',
      onClick: () => navigate('/ai-chat-assistant')
    },
    {
      icon: 'Users',
      title: 'Team Management',
      description: 'Multi-user accounts with role-based access and collaborative workflow management.',
      onClick: () => navigate('/team-performance-hub')
    },
    {
      icon: 'Target',
      title: 'Custom IPM Plans',
      description: 'AI-generated treatment rotations based on crop type, location, and growing conditions.',
      onClick: () => navigate('/premium-calculator-suite')
    },
    {
      icon: 'TrendingUp',
      title: 'Performance Analytics',
      description: 'Advanced metrics tracking with predictive modeling and optimization recommendations.',
      onClick: () => navigate('/treatment-analytics')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Add Large GG Logo at Top */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img 
                src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                alt="GrowGuardian Logo - AI-Powered Agricultural Companion"
                className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain"
              />
            </div>
          </div>

          <div className="text-center mb-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-6 leading-tight">
              AI-Powered IPM for
              <span className="block text-primary mt-2">Sustainable Agriculture</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
              Revolutionize your crop management with intelligent pest monitoring, predictive analytics, real-time field tracking, automated workflows, team collaboration, compliance management, and mobile-ready tools—all powered by advanced AI.
            </p>
            
            {/* Mobile App Store Links - Professional Design */}
            <div className="mb-8">
              <p className="text-base sm:text-lg font-semibold text-foreground mb-4">
                Download our mobile app:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                {/* App Store Button */}
                <a
                  href="https://apps.apple.com/us/app/grow-guardian/id6756507197"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex items-center bg-black hover:bg-gray-900 text-white pl-4 pr-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto max-w-xs"
                >
                  <div className="flex items-center space-x-3">
                    {/* Apple Icon */}
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-xs leading-tight opacity-90">Download on the</div>
                      <div className="text-base sm:text-lg font-semibold leading-tight">App Store</div>
                    </div>
                  </div>
                </a>

                {/* Google Play Button */}
                <a 
                  href="#" 
                  className="group relative flex items-center bg-black hover:bg-gray-900 text-white pl-4 pr-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl opacity-50 cursor-not-allowed w-full sm:w-auto max-w-xs"
                  onClick={(e) => e?.preventDefault()}
                >
                  <div className="flex items-center space-x-3">
                    {/* Google Play Icon */}
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-xs leading-tight opacity-90">GET IT ON</div>
                      <div className="text-base sm:text-lg font-semibold leading-tight">Google Play</div>
                    </div>
                  </div>
                  <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
                    Soon
                  </div>
                </a>
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex items-center justify-center gap-2">
                <Icon name="Smartphone" size={16} className="text-primary flex-shrink-0" />
                <span>Available on iOS · Android coming soon</span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <Link to="/pricing-page">
                <Button size="lg" className="px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/ai-chat-assistant">
                <Button variant="outline" size="lg" className="px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg flex items-center gap-2 w-full sm:w-auto">
                  <Icon name="MessageSquare" size={20} className="flex-shrink-0" />
                  <span>Try AI Chat Assistant</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* AI Chat Assistant CTA with Icon */}
          <div className="mb-16 bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <Link to="/ai-chat-assistant">
                <div className="p-4 bg-primary rounded-full cursor-pointer hover:bg-primary/90 transition-colors">
                  <Icon name="Bot" size={40} className="text-white" />
                </div>
              </Link>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Meet Your AI IPM Assistant
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Get instant answers about pest identification, treatment recommendations, 
              and compliance requirements. Available 24/7 with expert IPM knowledge.
            </p>
            <Link to="/ai-chat-assistant">
              <Button size="lg" className="px-8 py-4 text-lg flex items-center gap-3 mx-auto">
                <Icon name="MessageSquare" size={20} />
                Chat with AI Assistant
                <Icon name="ArrowRight" size={16} />
              </Button>
            </Link>
          </div>

          {/* NEW: AI Treatment Plan Workflow Section */}
          <div className="mb-16 bg-gradient-to-br from-blue-50 via-green-50 to-purple-50 border-2 border-primary/30 rounded-3xl p-8 lg:p-12 shadow-xl">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="relative">
                  <div className="p-6 bg-gradient-to-br from-primary to-success rounded-full">
                    <Icon name="Brain" size={48} className="text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                    <Icon name="Sparkles" size={16} className="text-yellow-800" />
                  </div>
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                🚀 Complete AI Treatment Plan Workflow
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto mb-8">
                From photo capture to full treatment success - AI creates personalized treatment plans 
                with product recommendations, application rates, and continuous progress tracking.
              </p>
            </div>

            {/* Treatment Workflow Steps */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
              {/* Step 1: Photo Capture */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-6 border border-green-200 shadow-lg h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Icon name="Camera" size={24} className="text-green-600" />
                    </div>
                    <div className="text-sm font-bold text-green-600 bg-green-100 px-3 py-1 rounded-full">Step 1</div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">📸 Photo Capture</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Simply upload photos of affected plants or pest issues. AI analyzes leaf damage, pest presence, and plant health indicators.
                  </p>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-green-800 mb-1">✅ AI Detection:</div>
                    <div className="text-xs text-green-700">• Pest identification</div>
                    <div className="text-xs text-green-700">• Disease recognition</div>
                    <div className="text-xs text-green-700">• Damage assessment</div>
                  </div>
                </div>
                {/* Arrow */}
                <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="ArrowRight" size={14} className="text-white" />
                  </div>
                </div>
              </div>

              {/* Step 2: AI Analysis */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-6 border border-blue-200 shadow-lg h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Icon name="Brain" size={24} className="text-blue-600" />
                    </div>
                    <div className="text-sm font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Step 2</div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">🧠 AI Analysis</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Advanced AI processes your photos with 95%+ accuracy, identifying specific pests, diseases, and severity levels.
                  </p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-blue-800 mb-1">🎯 Results Include:</div>
                    <div className="text-xs text-blue-700">• Confidence scoring</div>
                    <div className="text-xs text-blue-700">• Pressure level rating</div>
                    <div className="text-xs text-blue-700">• Risk assessment</div>
                  </div>
                </div>
                {/* Arrow */}
                <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="ArrowRight" size={14} className="text-white" />
                  </div>
                </div>
              </div>

              {/* Step 3: Treatment Plan */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-6 border border-purple-200 shadow-lg h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Icon name="FileText" size={24} className="text-purple-600" />
                    </div>
                    <div className="text-sm font-bold text-purple-600 bg-purple-100 px-3 py-1 rounded-full">Step 3</div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">📋 Treatment Plan</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    AI generates personalized treatment plans with specific product recommendations and precise application rates.
                  </p>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-purple-800 mb-1">💡 Includes:</div>
                    <div className="text-xs text-purple-700">• Product suggestions</div>
                    <div className="text-xs text-purple-700">• Application rates</div>
                    <div className="text-xs text-purple-700">• Timing schedules</div>
                  </div>
                </div>
                {/* Arrow */}
                <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="ArrowRight" size={14} className="text-white" />
                  </div>
                </div>
              </div>

              {/* Step 4: Progress Tracking */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-6 border border-orange-200 shadow-lg h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Icon name="TrendingUp" size={24} className="text-orange-600" />
                    </div>
                    <div className="text-sm font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-full">Step 4</div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">📈 Progress Tracking</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Monitor treatment effectiveness with follow-up photos and analytics showing real improvement over time.
                  </p>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-orange-800 mb-1">📊 Track:</div>
                    <div className="text-xs text-orange-700">• Treatment success</div>
                    <div className="text-xs text-orange-700">• Photo comparisons</div>
                    <div className="text-xs text-orange-700">• ROI analysis</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Camera Tracking Feature */}
            <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border-2 border-yellow-400/30 rounded-2xl p-8 mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-3 bg-yellow-400 rounded-lg">
                      <Icon name="Crown" size={24} className="text-yellow-900" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground">⭐ Premium Camera Tracking</h3>
                  </div>
                  <p className="text-lg text-muted-foreground mb-6">
                    Premium users get advanced photo tracking capabilities to monitor treatment progress with before/after comparisons and success analytics.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="Camera" size={20} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-foreground font-medium">Time-lapse treatment progress photos</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="BarChart3" size={20} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-foreground font-medium">Visual improvement analytics and scoring</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Calendar" size={20} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-foreground font-medium">Automated photo reminders and scheduling</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Image" size={20} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-foreground font-medium">Unlimited photo storage and history</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="font-bold text-foreground mb-4 text-center">📸 Treatment Progress Example</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-red-50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium text-foreground">Day 1: Pest Detected</span>
                      </div>
                      <span className="text-sm text-red-600 font-medium">High Pressure</span>
                    </div>
                    <div className="flex items-center justify-between bg-yellow-50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium text-foreground">Day 7: Treatment Applied</span>
                      </div>
                      <span className="text-sm text-yellow-600 font-medium">Improving</span>
                    </div>
                    <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-foreground">Day 14: Success!</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">87% Improved</span>
                    </div>
                  </div>
                  <div className="mt-4 bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-3 text-center">
                    <div className="text-sm font-bold text-foreground">🏆 Treatment Success Rate: 94%</div>
                    <div className="text-xs text-muted-foreground">Based on visual AI analysis</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Experience the Complete AI Treatment Journey
              </h3>
              <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
                Join thousands of growers using AI-powered treatment plans to achieve 87% better pest control results with precise product recommendations and application rates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={() => navigate('/ai-chat-assistant')}
                  size="lg"
                  className="px-8 py-4 text-lg bg-gradient-to-r from-primary to-success hover:opacity-90"
                >
                  <Icon name="Camera" size={20} className="mr-2" />
                  Start AI Photo Analysis
                  <Icon name="ArrowRight" size={16} className="ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/premium-calculator-suite')}
                  variant="outline"
                  className="px-8 py-4 text-lg border-2 border-primary text-primary hover:bg-primary/5"
                >
                  <Icon name="Crown" size={20} className="mr-2" />
                  See Premium Tracking Features
                </Button>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">95%+</div>
                  <div className="text-sm text-muted-foreground">AI Accuracy Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">87%</div>
                  <div className="text-sm text-muted-foreground">Better Control Results</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">AI Availability</div>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Quick Access Navigation Section - Prominently Display All Pages */}
          <div className="mb-16 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 border-2 border-primary/20 rounded-3xl p-8 lg:p-12 shadow-xl">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="p-4 bg-gradient-to-br from-primary to-success rounded-full">
                  <Icon name="Layout" size={48} className="text-white" />
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                🚀 Explore All Features & Dashboards
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto mb-8">
                Complete suite of professional IPM tools at your fingertips. Click any card to explore the full capabilities of GrowGuardian.
              </p>
            </div>

            {/* Complete Navigation Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              
              {/* Dashboard */}
              <button
                onClick={() => navigate('/ipm-operations-overview')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Icon name="BarChart3" size={28} className="text-primary" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-primary transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">IPM Operations Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-3">Real-time operations overview with KPIs, field status, and weather integration</p>
                <div className="flex items-center text-xs text-primary font-medium">
                  <span>Explore Dashboard</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Task Management */}
              <button
                onClick={() => navigate('/task-management')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-success/10 rounded-lg group-hover:bg-success/20 transition-colors">
                    <Icon name="CheckSquare" size={28} className="text-success" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-success transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Task Management</h3>
                <p className="text-sm text-muted-foreground mb-3">Organize and track all IPM tasks with scheduling and team assignments</p>
                <div className="flex items-center text-xs text-success font-medium">
                  <span>Manage Tasks</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Team Performance */}
              <button
                onClick={() => navigate('/team-performance-hub')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-accent/10 rounded-lg group-hover:bg-accent/20 transition-colors">
                    <Icon name="Users" size={28} className="text-accent" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-accent transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Team Performance Hub</h3>
                <p className="text-sm text-muted-foreground mb-3">Monitor team productivity with performance metrics and leaderboards</p>
                <div className="flex items-center text-xs text-accent font-medium">
                  <span>View Team Stats</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Treatment Analytics */}
              <button
                onClick={() => navigate('/treatment-analytics')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                    <Icon name="TrendingUp" size={28} className="text-purple-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-purple-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Treatment Analytics</h3>
                <p className="text-sm text-muted-foreground mb-3">Comprehensive analytics with effectiveness tracking and ROI analysis</p>
                <div className="flex items-center text-xs text-purple-600 font-medium">
                  <span>Analyze Treatments</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Product Browser */}
              <button
                onClick={() => navigate('/product-browser')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                    <Icon name="Package" size={28} className="text-orange-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-orange-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Product Browser</h3>
                <p className="text-sm text-muted-foreground mb-3">Searchable database of IPM products with detailed information</p>
                <div className="flex items-center text-xs text-orange-600 font-medium">
                  <span>Browse Products</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Premium Calculator Suite */}
              <button
                onClick={() => navigate('/premium-calculator-suite')}
                className="group bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 shadow-sm border-2 border-yellow-300/50 hover:shadow-lg hover:border-yellow-400 transition-all text-left relative overflow-hidden"
              >
                <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                  ⭐ Premium
                </div>
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-yellow-100 rounded-lg group-hover:bg-yellow-200 transition-colors">
                    <Icon name="Calculator" size={28} className="text-yellow-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-yellow-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Premium Calculators</h3>
                <p className="text-sm text-muted-foreground mb-3">Advanced beneficial release rates, chemical mixing, and IPM planning</p>
                <div className="flex items-center text-xs text-yellow-700 font-medium">
                  <span>Access Calculators</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Compliance Dashboard */}
              <button
                onClick={() => navigate('/compliance-dashboard')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <Icon name="Shield" size={28} className="text-blue-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Compliance Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-3">Track regulatory compliance with automated reporting and alerts</p>
                <div className="flex items-center text-xs text-blue-600 font-medium">
                  <span>View Compliance</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Application Logs */}
              <button
                onClick={() => navigate('/application-logs')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                    <Icon name="FileText" size={28} className="text-teal-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-teal-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Application Logs</h3>
                <p className="text-sm text-muted-foreground mb-3">Complete record of all pesticide applications and treatments</p>
                <div className="flex items-center text-xs text-teal-600 font-medium">
                  <span>View Logs</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* AI Chat Assistant */}
              <button
                onClick={() => navigate('/ai-chat-assistant')}
                className="group bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 shadow-sm border-2 border-primary/30 hover:shadow-lg hover:border-primary transition-all text-left relative overflow-hidden"
              >
                <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                  ⭐ Premium
                </div>
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Icon name="MessageSquare" size={28} className="text-primary" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-primary transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">AI Chat Assistant</h3>
                <p className="text-sm text-muted-foreground mb-3">24/7 AI-powered IPM expert with photo diagnosis and recommendations</p>
                <div className="flex items-center text-xs text-primary font-medium">
                  <span>Chat with AI</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* IPM Learning Center */}
              <button
                onClick={() => navigate('/ipm-learning-center')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                    <Icon name="BookOpen" size={28} className="text-indigo-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">IPM Learning Center</h3>
                <p className="text-sm text-muted-foreground mb-3">Comprehensive educational resources and pest identification guides</p>
                <div className="flex items-center text-xs text-indigo-600 font-medium">
                  <span>Start Learning</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Audit Log Dashboard */}
              <button
                onClick={() => navigate('/audit-log-dashboard')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                    <Icon name="Shield" size={28} className="text-gray-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Audit Log Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-3">Complete system activity tracking and security audit trails</p>
                <div className="flex items-center text-xs text-gray-600 font-medium">
                  <span>View Audit Logs</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Unified Notification Center */}
              <button
                onClick={() => navigate('/unified-notification-center')}
                className="group bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                    <Icon name="Bell" size={28} className="text-red-600" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-red-600 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Notification Center</h3>
                <p className="text-sm text-muted-foreground mb-3">Centralized alert management with priority filtering and trends</p>
                <div className="flex items-center text-xs text-red-600 font-medium">
                  <span>Manage Notifications</span>
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </div>
              </button>

              {/* Scouting Logs - Mobile App Feature */}
              <div
                className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-sm border-2 border-green-300/50 hover:shadow-lg hover:border-green-400 transition-all text-left relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                    <Icon name="Search" size={28} className="text-green-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-green-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Scouting Logs</h3>
                <p className="text-sm text-muted-foreground">Field scouting reports with pest observations and photo documentation</p>
              </div>

              {/* Grow Logs - Mobile App Feature */}
              <div
                className="group bg-gradient-to-br from-lime-50 to-green-50 rounded-xl p-6 shadow-sm border-2 border-lime-300/50 hover:shadow-lg hover:border-lime-400 transition-all text-left relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-lime-100 rounded-lg group-hover:bg-lime-200 transition-colors">
                    <Icon name="Sprout" size={28} className="text-lime-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-lime-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Grow Logs</h3>
                <p className="text-sm text-muted-foreground">Track crop growth stages, environmental conditions, and cultivation activities</p>
              </div>

              {/* Inventory Logs - Mobile App Feature */}
              <div
                className="group bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 shadow-sm border-2 border-cyan-300/50 hover:shadow-lg hover:border-cyan-400 transition-all text-left relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-cyan-100 rounded-lg group-hover:bg-cyan-200 transition-colors">
                    <Icon name="Database" size={28} className="text-cyan-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-cyan-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Inventory Logs</h3>
                <p className="text-sm text-muted-foreground">Real-time inventory tracking for pesticides, beneficials, and supplies</p>
              </div>

              {/* Field Operations - Mobile App Feature */}
              <div
                className="group bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 shadow-sm border-2 border-amber-300/50 hover:shadow-lg hover:border-amber-400 transition-all text-left relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
                    <Icon name="Tractor" size={28} className="text-amber-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-amber-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Field Operations</h3>
                <p className="text-sm text-muted-foreground">Coordinate field activities, equipment usage, and operational workflows</p>
              </div>

              {/* Predictive AI Pest & Fungal Management - Mobile App Feature */}
              <div
                className="group bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 shadow-sm border-2 border-violet-300/50 hover:shadow-lg hover:border-violet-400 transition-all text-left relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-violet-100 rounded-lg group-hover:bg-violet-200 transition-colors">
                    <Icon name="Brain" size={28} className="text-violet-700" />
                  </div>
                  <Icon name="ArrowRight" size={18} className="text-gray-400 group-hover:text-violet-700 transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Predictive AI Pest & Fungal Management</h3>
                <p className="text-sm text-muted-foreground">AI-powered predictions for pest pressure and fungal pathogen outbreaks</p>
              </div>

            </div>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <div className="bg-gradient-to-r from-primary/10 to-success/10 rounded-2xl p-8 border border-primary/20">
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Ready to Transform Your IPM Management?
                </h3>
                <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Get instant access to all features with a 14-day free trial. No credit card required.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button
                    onClick={() => navigate('/login')}
                    size="lg"
                    className="px-8 py-4 text-lg bg-gradient-to-r from-primary to-success hover:opacity-90"
                  >
                    <Icon name="Rocket" size={20} className="mr-2" />
                    Start Free Trial
                    <Icon name="ArrowRight" size={16} className="ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate('/ai-chat-assistant')}
                    variant="outline"
                    className="px-8 py-4 text-lg border-2 border-primary text-primary hover:bg-primary/5"
                  >
                    <Icon name="MessageSquare" size={20} className="mr-2" />
                    Try AI Assistant First
                  </Button>
                </div>
                <div className="mt-6 flex flex-wrap gap-4 justify-center items-center text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>No credit card required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Full feature access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Cancel anytime</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid - FIXED RESPONSIVE LAYOUT */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
            <div className="bg-card rounded-xl p-6 border border-border agricultural-shadow">
              <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                <Icon name="Brain" size={24} className="text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 leading-tight">AI Plant Diagnosis</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed break-words">
                Upload photos for instant pest and disease identification with treatment recommendations
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border agricultural-shadow">
              <div className="p-3 bg-success/10 rounded-lg w-fit mb-4">
                <Icon name="Shield" size={24} className="text-success" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 leading-tight">IPM Compliance</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed break-words">
                Automated compliance tracking and reporting for regulatory requirements
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border agricultural-shadow">
              <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                <Icon name="BarChart3" size={24} className="text-accent" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 leading-tight">Treatment Analytics</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed break-words">
                Comprehensive analysis of treatment effectiveness and ROI optimization
              </p>
            </div>
          </div>

          {/* Environmental Controls Integration Section - FIXED GRID */}
          <div className="mb-16 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200/50 rounded-2xl p-6 sm:p-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-blue-600 rounded-full">
                  <Icon name="Thermometer" size={32} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-4 leading-tight">
                🌡️ Environmental Controls Integration
              </h3>
              <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-6 leading-relaxed">
                Connect your existing environmental monitoring systems to make data-driven treatment decisions. 
                GrowGuardian integrates seamlessly with industry-leading control platforms.
              </p>
            </div>
            
            {/* FIXED: Reduce columns on laptop screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">
              <div className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-blue-100">
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Zap" size={20} className="text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground text-sm sm:text-base leading-tight mb-1">Pulse Grow Integration</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">Real-time environmental data</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                  Monitor temperature, humidity, and VPD to optimize treatment timing and effectiveness
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-blue-100">
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Link" size={20} className="text-green-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground text-sm sm:text-base leading-tight mb-1">GrowLink Connectivity</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">Automated climate control</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                  Sync environmental conditions with IPM schedules for maximum pest control efficiency
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-blue-100">
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Settings" size={20} className="text-orange-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground text-sm sm:text-base leading-tight mb-1">TrolMaster Support</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">Comprehensive monitoring</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                  Access detailed environmental metrics to determine optimal treatment windows
                </p>
              </div>

              <div className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-blue-100">
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Cpu" size={20} className="text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground text-sm sm:text-base leading-tight mb-1">AC Infinity Controller 69 Pro</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">Advanced environmental control</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                  Popular control system integration for precise climate management and IPM optimization
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600/10 to-green-600/10 rounded-xl p-6 border border-blue-200/30">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h4 className="text-xl font-bold text-foreground mb-4">
                    Data-Driven Treatment Decisions
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">Analyze humidity levels before beneficial releases</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">Time spray applications with optimal VPD conditions</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">Track temperature patterns for pest lifecycle predictions</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">Correlate environmental changes with pest pressure spikes</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-semibold text-foreground mb-4 text-center">Environmental Trigger Example</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-yellow-50 rounded p-3">
                      <span className="text-sm font-medium text-yellow-800">High Humidity Alert</span>
                      <span className="text-sm text-yellow-600">85% RH</span>
                    </div>
                    <div className="flex justify-between items-center bg-blue-50 rounded p-3">
                      <span className="text-sm font-medium text-blue-800">AI Recommendation</span>
                      <span className="text-sm text-blue-600">Delay spray 6hrs</span>
                    </div>
                    <div className="flex justify-between items-center bg-green-50 rounded p-3">
                      <span className="text-sm font-medium text-green-800">Optimal Window</span>
                      <span className="text-sm text-green-600">Tomorrow 6AM</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-muted-foreground mb-4">
                Already using environmental controls? Connect them to GrowGuardian for smarter IPM decisions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/premium-calculator-suite')}
                  className="px-6 py-3"
                >
                  See Integration Setup
                </Button>
                <Button
                  onClick={() => navigate('/ai-chat-assistant')}
                  variant="outline"
                  className="px-6 py-3"
                >
                  Ask AI About Your Setup
                </Button>
              </div>
            </div>
          </div>

          {/* See it In Action */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">See it In Action</h2>
              <p className="text-xl text-muted-foreground">
                Experience how our AI-powered platform transforms agricultural management
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button
                onClick={() => setActiveDemo('ai-diagnosis')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeDemo === 'ai-diagnosis' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                AI Plant Diagnosis
              </button>
              <button
                onClick={() => setActiveDemo('treatment-analytics')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeDemo === 'treatment-analytics' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Treatment Analytics
              </button>
              <button
                onClick={() => setActiveDemo('calculators')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeDemo === 'calculators' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                IPM Calculators
              </button>
            </div>

            {/* Add Full Preview Links */}
            <div className="text-center mb-8">
              <p className="text-muted-foreground mb-4">Want to explore more? Try our interactive previews:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/preview/treatment-analytics')}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Icon name="BarChart3" size={16} className="mr-2" />
                  Treatment Analytics Demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/preview/team-performance')}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Icon name="Users" size={16} className="mr-2" />
                  Team Performance Demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/preview/calculators')}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Icon name="Calculator" size={16} className="mr-2" />
                  Calculators Demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/preview/notifications')}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Icon name="Bell" size={16} className="mr-2" />
                  Notifications Demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/preview/audit-logs')}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Icon name="Shield" size={16} className="mr-2" />
                  Audit Logs Demo
                </Button>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border agricultural-shadow overflow-hidden">
              {activeDemo === 'ai-diagnosis' && (
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">
                        AI Plant Diagnosis Demo
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Upload" size={20} className="text-primary" />
                            <span className="font-medium">Upload Photo</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Simply upload a photo of your affected plant
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Brain" size={20} className="text-success" />
                            <span className="font-medium">AI Analysis</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Advanced AI identifies pests and diseases instantly
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Target" size={20} className="text-accent" />
                            <span className="font-medium">Treatment Plan</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Receive targeted IPM recommendations and product suggestions
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-primary/10 to-success/10 rounded-xl p-6">
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm">Diagnosis Results</span>
                          <span className="px-2 py-1 bg-success/20 text-success rounded text-sm">95% Confidence</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Spider Mites</span>
                            <span className="text-sm font-medium text-error">High Pressure</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Powdery Mildew</span>
                            <span className="text-sm font-medium text-warning">Medium Risk</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-semibold mb-4 text-center">Recommended Actions</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Release Phytoseiulus persimilis</li>
                          <li>• Apply neem oil spray (early morning)</li>
                          <li>• Increase humidity to 65-70%</li>
                          <li>• Monitor weekly for 3 weeks</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeDemo === 'treatment-analytics' && (
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">
                        Treatment Analytics Dashboard
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="BarChart3" size={20} className="text-primary" />
                            <span className="font-medium">Effectiveness Tracking</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Monitor treatment success rates and ROI across all fields
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="TrendingUp" size={20} className="text-success" />
                            <span className="font-medium">Predictive Insights</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            AI-powered predictions for optimal treatment timing
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Download" size={20} className="text-accent" />
                            <span className="font-medium">Export Reports</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Generate detailed compliance and performance reports
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6">
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm">Treatment Success Rate</span>
                          <span className="px-2 py-1 bg-success/20 text-success rounded text-sm">94.2%</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Week 1:</span>
                            <span className="text-sm font-medium text-success">87%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Week 2:</span>
                            <span className="text-sm font-medium text-success">91%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Week 3:</span>
                            <span className="text-sm font-medium text-success">94%</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Top Performing Treatments</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Beneficial Release Program</span>
                            <span className="text-sm font-medium text-success">97.8%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Organic Spray Rotation</span>
                            <span className="text-sm font-medium text-success">92.4%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Cultural Controls</span>
                            <span className="text-sm font-medium text-primary">88.9%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeDemo === 'calculators' && (
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">
                        IPM Calculator Suite
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Calculator" size={20} className="text-primary" />
                            <span className="font-medium">Chemical Calculator</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Precise mixing ratios and application rates for safe usage
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="Bug" size={20} className="text-success" />
                            <span className="font-medium">Beneficial Calculator</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Optimal release rates for predatory insects and mites
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon name="FileText" size={20} className="text-accent" />
                            <span className="font-medium">IPM Plan Generator</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Automated IPM plan creation based on crop and location
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-success/10 to-accent/10 rounded-xl p-6">
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm">Chemical Calculator</span>
                          <span className="text-sm font-medium text-primary">3.2 gallons</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Product: Neem Oil 70%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Rate: 2 fl oz/gallon</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Area: 1.5 acres</span>
                          </div>
                          <div className="border-t pt-2 font-semibold">
                            <div className="flex justify-between">
                              <span className="text-sm">Total Needed:</span>
                              <span className="text-primary">3.2 gallons</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-semibold mb-2">Safety Reminders</h4>
                        <ul className="text-sm space-y-1 text-muted-foreground">
                          <li>• PPE required during application</li>
                          <li>• REI: 4 hours</li>
                          <li>• PHI: 0 days (harvest safe)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* About Grow Guardian - Pulse Grow Section - FIRST EXPLANATORY SECTION */}
      <section id="about-grow-guardian" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-green-50 to-blue-50 border-b-4 border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Leaf" size={28} className="text-green-600 flex-shrink-0" />
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">About Grow Guardian AI</h2>
            </div>
            
            {/* What We Are - Core Identity - FIXED GRID */}
            <div className="mb-10">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">What We Are</h3>
              {/* FIXED: Better laptop breakpoints */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-green-100">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Brain" size={24} className="text-green-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 leading-tight">AI Agricultural Intelligence</h4>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    Advanced machine learning models trained on thousands of successful growing operations to provide intelligent, crop-specific recommendations.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-blue-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 leading-tight">Built by Real Growers</h4>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    Created by agricultural professionals with hands-on experience in commercial vegetables, fruits, and cannabis operations.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-purple-100">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Target" size={24} className="text-purple-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 leading-tight">Precision IPM Solutions</h4>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    Comprehensive integrated pest management tools that scale from home gardens to commercial greenhouse operations.
                  </p>
                </div>
              </div>
            </div>

            {/* All Our Features - FIXED GRID LAYOUT */}
            <div className="mb-10">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8">Complete Feature Suite</h3>
              {/* FIXED: Better responsive grid for laptop screens */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
                
                {/* Core AI Features */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 sm:p-6 border border-green-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Camera" size={20} className="text-green-600 flex-shrink-0" />
                    <h4 className="font-semibold text-green-900 text-sm sm:text-base leading-tight">AI Plant Diagnosis</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-green-800 leading-relaxed">
                    <li className="break-words">• Instant pest and disease identification</li>
                    <li className="break-words">• 95%+ accuracy with confidence scoring</li>
                    <li className="break-words">• Treatment recommendations from product database</li>
                    <li className="break-words">• Photo history and progress tracking</li>
                  </ul>
                </div>

                {/* Advanced Calculators */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 sm:p-6 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Calculator" size={20} className="text-blue-600 flex-shrink-0" />
                    <h4 className="font-semibold text-blue-900 text-sm sm:text-base leading-tight">Professional Calculators</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-blue-800 leading-relaxed">
                    <li className="break-words">• Beneficial insect release rate calculator</li>
                    <li className="break-words">• Chemical mixing ratio calculator</li>
                    <li className="break-words">• Tank mixing compatibility checker</li>
                    <li className="break-words">• Custom IPM plan generator</li>
                  </ul>
                </div>

                {/* Analytics & Tracking */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 sm:p-6 border border-purple-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="BarChart3" size={20} className="text-purple-600 flex-shrink-0" />
                    <h4 className="font-semibold text-purple-900 text-sm sm:text-base leading-tight">Treatment Analytics</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-purple-800 leading-relaxed">
                    <li className="break-words">• Treatment effectiveness tracking</li>
                    <li className="break-words">• ROI analysis and cost optimization</li>
                    <li className="break-words">• Predictive pest pressure modeling</li>
                    <li className="break-words">• Field performance heat maps</li>
                  </ul>
                </div>

                {/* Compliance & Documentation */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5 sm:p-6 border border-orange-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Shield" size={20} className="text-orange-600 flex-shrink-0" />
                    <h4 className="font-semibold text-orange-900 text-sm sm:text-base leading-tight">Compliance Management</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-orange-800 leading-relaxed">
                    <li className="break-words">• Automated application logging</li>
                    <li className="break-words">• Regulatory compliance tracking</li>
                    <li className="break-words">• Organic certification support</li>
                    <li className="break-words">• Export capabilities for audits</li>
                  </ul>
                </div>

                {/* Team & Task Management */}
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-5 sm:p-6 border border-teal-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Users" size={20} className="text-teal-600 flex-shrink-0" />
                    <h4 className="font-semibold text-teal-900 text-sm sm:text-base leading-tight">Team Collaboration</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-teal-800 leading-relaxed">
                    <li className="break-words">• Multi-user account management</li>
                    <li className="break-words">• Role-based access controls</li>
                    <li className="break-words">• Task scheduling and assignments</li>
                    <li className="break-words">• Team performance analytics</li>
                  </ul>
                </div>

                {/* Environmental Integration */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 sm:p-6 border border-indigo-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="Thermometer" size={20} className="text-indigo-600 flex-shrink-0" />
                    <h4 className="font-semibold text-indigo-900 text-sm sm:text-base leading-tight">Environmental Controls</h4>
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-indigo-800 leading-relaxed">
                    <li className="break-words">• Pulse Grow, GrowLink, TrolMaster integration</li>
                    <li className="break-words">• Environmental data analysis</li>
                    <li className="break-words">• Optimal treatment timing</li>
                    <li className="break-words">• Climate-responsive IPM scheduling</li>
                  </ul>
                </div>

              </div>
            </div>

            {/* Mission Statement */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-lg text-green-100 mb-6 leading-relaxed">
                Grow Guardian AI empowers growers of all scales—from home gardeners to commercial operations—with professional-grade 
                IPM tools that were previously only available to large agricultural enterprises.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-yellow-300 mb-2">87%</div>
                  <div className="text-sm text-green-100">Average pest control improvement</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-300 mb-2">$2,400+</div>
                  <div className="text-sm text-green-100">Annual cost savings</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-300 mb-2">24/7</div>
                  <div className="text-sm text-green-100">AI assistant availability</div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-10 text-center">
              <p className="text-lg text-gray-600 mb-6">
                Ready to experience the future of agricultural pest management?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => scrollToSection('features')}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
                >
                  Explore All Features
                </Button>
                <Button
                  onClick={() => navigate('/ai-chat-assistant')}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3 text-lg"
                >
                  Try Grow Guardian AI
                </Button>
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <Icon name="Leaf" size={24} className="text-green-600 sm:w-8 sm:h-8" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">About GrowGuardian</h2>
            </div>
            
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed px-4">
              GrowGuardian is your complete AI-powered agricultural companion designed for vegetables, fruits, and cannabis crops. 
              Whether you're a commercial grower, greenhouse operator, or home cultivator, our intelligent system 
              provides professional-grade accuracy with comprehensive IPM solutions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              <div className="text-left">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 leading-tight break-words">From home gardens to commercial operations:</h3>
                <ul className="space-y-2 sm:space-y-3 px-4 md:px-0">
                  {[
                    'AI-powered plant diagnosis and pest identification',
                    'Real-time treatment recommendations from product database',
                    'Professional IPM planning and scheduling',
                    'Team collaboration and task management',
                    'Advanced analytics and compliance reporting'
                  ]?.map((item, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 text-sm sm:text-base break-words">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 sm:p-6 mx-4 md:mx-0">
                <h3 className="text-xl sm:text-2xl font-bold text-green-900 mb-3 sm:mb-4 break-words">
                  Professional-grade tools for any scale
                </h3>
                <p className="text-green-700 mb-3 sm:mb-4 text-sm sm:text-base break-words">
                  Built by growers for growers. Our AI system combines real agricultural experience with 
                  cutting-edge technology for vegetables, fruits, and cannabis cultivation.
                </p>
                <Button
                  onClick={() => scrollToSection('features')}
                  className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                >
                  Explore Features
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section - Personal Grower FREE */}
      <section id="features" className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <Icon name="Zap" size={24} className="text-green-600 sm:w-8 sm:h-8" />
              <h2 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-gray-900 leading-tight">🆓 Personal Grower Plan (FREE)</h2>
            </div>
            <p className="text-base sm:text-lg text-gray-600 px-4">Essential IPM Tools - Forever Free</p>
          </div>

          {/* FIXED: Better grid for laptop screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
            {features?.map((feature, index) => (
              <button
                key={index}
                onClick={feature?.onClick}
                className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full min-h-[180px] flex flex-col"
              >
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={feature?.icon} size={20} className="text-green-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight flex-1 break-words">{feature?.title}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm flex-1 break-words">{feature?.description}</p>
              </button>
            ))}
          </div>

          {/* Data Storage FREE Plan - FIXED GRID */}
          <div className="bg-white rounded-lg p-5 sm:p-8 shadow-sm border border-gray-200">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">Data Storage:</h3>
            {/* FIXED: Better grid for laptop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">
              <div className="text-center">
                <Icon name="Calendar" size={24} className="text-green-600 mx-auto mb-2 flex-shrink-0" />
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 leading-tight">Last 30 days</h4>
                <p className="text-xs sm:text-sm text-gray-600 break-words">Application logs access</p>
              </div>
              <div className="text-center">
                <Icon name="Clock" size={24} className="text-green-600 mx-auto mb-2 flex-shrink-0" />
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 leading-tight">Basic treatment history</h4>
                <p className="text-xs sm:text-sm text-gray-600 break-words">Recent treatments only</p>
              </div>
              <div className="text-center">
                <Icon name="Image" size={24} className="text-green-600 mx-auto mb-2 flex-shrink-0" />
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 leading-tight">Limited photo storage</h4>
                <p className="text-xs sm:text-sm text-gray-600 break-words">Last 10 plant diagnoses</p>
              </div>
              <div className="text-center">
                <Icon name="Database" size={24} className="text-green-600 mx-auto mb-2 flex-shrink-0" />
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 leading-tight">Product database</h4>
                <p className="text-xs sm:text-sm text-gray-600 break-words">Browsing access</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Premium Section - Commercial Operation */}
      <section id="premium" className="py-12 sm:py-16 bg-gradient-to-b from-green-900 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <Icon name="Crown" size={24} className="text-yellow-400 sm:w-8 sm:h-8" />
              <h2 className="text-2xl sm:text-3xl font-bold">💎 Commercial Operation ($14.99/month)</h2>
            </div>
            <p className="text-lg sm:text-xl text-green-100 px-4 mb-4">Unlock Professional IPM Management + Save $50+ Monthly on Pest Control</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <div className="bg-yellow-400/20 border border-yellow-300/30 rounded-lg px-4 py-2">
                <p className="text-yellow-200 text-sm font-medium">
                  ⚡ 14-Day Free Trial - No Credit Card Required
                </p>
              </div>
              <div className="bg-green-500/20 border border-green-400/30 rounded-lg px-4 py-2">
                <p className="text-green-200 text-sm font-medium">
                  💰 ROI Guaranteed: Save More Than You Pay in Month 1
                </p>
              </div>
            </div>
          </div>

          {/* Value Proposition Banner */}
          <div className="bg-gradient-to-r from-yellow-600/30 to-orange-600/30 border border-yellow-400/30 rounded-xl p-6 mb-8 sm:mb-12">
            <div className="text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-yellow-200 mb-4">
                🚀 Transform Your Growing Operation in 30 Days
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-300">87%</div>
                  <div className="text-sm text-yellow-200">Average Pest Control Improvement</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-300">$2,400+</div>
                  <div className="text-sm text-yellow-200">Annual Savings on Treatment Costs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-300">15 min</div>
                  <div className="text-sm text-yellow-200">Daily Time Savings on IPM Tasks</div>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced IPM Tools with Tank Mixing - FIXED GRID */}
          <div className="mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">Advanced IPM Tools:</h3>
            {/* FIXED: Better grid for laptop screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
              {/* Tank Mixing Procedures - New Feature */}
              <button
                onClick={() => navigate('/premium-calculator-suite')}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-5 sm:p-6 border border-white/20 hover:bg-white/15 transition-colors text-left w-full min-h-[200px] flex flex-col"
              >
                <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                  <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="FlaskConical" size={20} className="text-green-900" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white leading-tight flex-1 break-words">⭐ Tank Mixing Procedures</h3>
                </div>
                <p className="text-green-100 leading-relaxed text-sm flex-1 break-words mb-3">
                  Professional tank mixing with compatibility checks, W.A.L.E.S. protocol, and phytotoxicity warnings for safe product combinations.
                </p>
                <div className="mt-auto bg-red-400/20 border border-red-300/30 rounded p-2">
                  <p className="text-red-200 text-xs font-medium break-words">
                    ⚠️ Compatibility alerts prevent dangerous mixes like M-Pede + Silver Bullet
                  </p>
                </div>
              </button>

              {/* Keep existing premium features with same fixes */}
              {premiumFeatures?.slice(0, 4)?.map((feature, index) => (
                <button
                  key={index}
                  onClick={feature?.onClick}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-5 sm:p-6 border border-white/20 hover:bg-white/15 transition-colors text-left w-full min-h-[180px] flex flex-col"
                >
                  <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                    <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={feature?.icon} size={20} className="text-green-900" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-white leading-tight flex-1 break-words">⭐ {feature?.title}</h3>
                  </div>
                  <p className="text-green-100 leading-relaxed text-sm flex-1 break-words">{feature?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Professional Management - FIXED GRID */}
          <div className="mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">Professional Management:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
              {premiumFeatures?.slice(5)?.map((feature, index) => (
                <button
                  key={index}
                  onClick={feature?.onClick}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-5 sm:p-6 border border-white/20 hover:bg-white/15 transition-colors text-left w-full min-h-[180px] flex flex-col"
                >
                  <div className="flex items-start space-x-3 mb-3 sm:mb-4">
                    <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={feature?.icon} size={20} className="text-green-900" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-white leading-tight flex-1 break-words">⭐ {feature?.title}</h3>
                  </div>
                  <p className="text-green-100 leading-relaxed text-sm flex-1 break-words">{feature?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Enterprise Data - FIXED GRID */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 sm:p-8 border border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">Enterprise Data:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
              <div className="text-center">
                <Icon name="Archive" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ 2+ Year Application Log History</h4>
                <p className="text-green-100 text-sm break-words">Complete treatment records</p>
              </div>
              <div className="text-center">
                <Icon name="Database" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ Unlimited Treatment Records</h4>
                <p className="text-green-100 text-sm break-words">No data limits</p>
              </div>
              <div className="text-center">
                <Icon name="Image" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ Complete Photo Archive</h4>
                <p className="text-green-100 text-sm break-words">All diagnosis and progress images</p>
              </div>
              <div className="text-center">
                <Icon name="Download" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ CSV Export Capabilities</h4>
                <p className="text-green-100 text-sm break-words">Data portability for external systems</p>
              </div>
              <div className="text-center">
                <Icon name="FileText" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ Compliance Reporting Tools</h4>
                <p className="text-green-100 text-sm break-words">Organic certification support</p>
              </div>
              <div className="text-center">
                <Icon name="Search" size={28} className="text-yellow-400 mx-auto mb-3 sm:mb-4 flex-shrink-0" />
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight break-words">⭐ Advanced Search & Filtering</h4>
                <p className="text-green-100 text-sm break-words">Find any historical data instantly</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* See It In Action Section - FIXED GRID */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <Icon name="Monitor" size={24} className="text-green-600 sm:w-8 sm:h-8" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">See It In Action</h2>
            </div>
            <p className="text-base sm:text-lg text-gray-600 px-4">Professional IPM tools demonstrated with real agricultural scenarios</p>
          </div>

          {/* FIXED: Better responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
            {/* All demo cards get same treatment - example for first one */}
            <button
              onClick={() => navigate('/treatment-analytics')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-blue-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock AI Camera Interface */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <Icon name="Camera" size={16} className="text-green-600" />
                        <span className="text-xs font-medium text-gray-800">AI Analysis</span>
                      </div>
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">95% Confidence</div>
                    </div>
                    <div className="bg-red-100 border border-red-200 rounded p-2 mb-2">
                      <div className="text-xs font-medium text-red-800 mb-1">🔍 Spider Mites Detected</div>
                      <div className="text-xs text-red-600">Pressure Level: 4/5 (High)</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <div className="text-xs font-medium text-blue-800 mb-1">💡 Treatment Rec:</div>
                      <div className="text-xs text-blue-600">Phytoseiulus persimilis + Zerotol</div>
                    </div>
                  </div>
                  <p className="text-xs text-green-800 font-medium">Real-time pest identification with treatment suggestions</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">AI Plant Diagnosis Camera</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">Instant pest detection with confidence scoring and immediate treatment recommendations from our IPM database.</p>
            </button>

            {/* Treatment Effectiveness Analytics Example */}
            <button
              onClick={() => navigate('/treatment-analytics')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock Analytics Dashboard */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm">
                    <div className="text-xs font-medium text-gray-800 mb-2 text-center">Treatment Success Rate</div>
                    <div className="flex justify-between items-end h-16 mb-2">
                      <div className="bg-green-500 w-3 h-12 rounded-t"></div>
                      <div className="bg-green-400 w-3 h-10 rounded-t"></div>
                      <div className="bg-yellow-400 w-3 h-6 rounded-t"></div>
                      <div className="bg-green-500 w-3 h-14 rounded-t"></div>
                      <div className="bg-green-600 w-3 h-16 rounded-t"></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>W1</span><span>W2</span><span>W3</span><span>W4</span><span>W5</span>
                    </div>
                    <div className="mt-2 bg-green-100 rounded p-1">
                      <div className="text-xs text-green-800 font-medium">87% Average Success</div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-800 font-medium mt-1">Track treatment effectiveness over time</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">Treatment Effectiveness Tracking</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">Monitor your IPM success rates with detailed analytics showing which treatments work best for your crops.</p>
            </button>

            {/* Beneficial Calculator Example */}
            <button
              onClick={() => navigate('/premium-calculator-suite')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 to-orange-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock Calculator Interface */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm">
                    <div className="text-xs font-medium text-gray-800 mb-2 flex items-center justify-center space-x-1">
                      <Icon name="Calculator" size={14} className="text-orange-600" />
                      <span>Beneficial Release Calculator</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Room Size:</span>
                        <span className="text-xs font-medium">20' x 30'</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Pest Pressure:</span>
                        <span className="text-xs font-medium text-red-600">4/5 (High)</span>
                      </div>
                      <div className="bg-green-100 rounded p-2">
                        <div className="text-xs font-medium text-green-800">Recommendation:</div>
                        <div className="text-xs text-green-700">Cucumeris: 15,000 per week</div>
                        <div className="text-xs text-green-700">Swirskii: 8,000 per week</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-orange-800 font-medium mt-1">Precise beneficial insect release rates</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">⭐ Beneficial & Chemical Calculators</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">Calculate exact release rates for beneficial insects and mixing ratios for chemical treatments based on your facility size and pest pressure.</p>
            </button>

            {/* Treatment Planner Example */}
            <button
              onClick={() => navigate('/task-management')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-pink-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock Calendar Interface */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm">
                    <div className="text-xs font-medium text-gray-800 mb-2 text-center">This Week's IPM Schedule</div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between bg-green-50 rounded p-1">
                        <span className="text-xs text-green-800">Mon: Regalia Spray</span>
                        <Icon name="CheckCircle" size={12} className="text-green-600" />
                      </div>
                      <div className="flex items-center justify-between bg-blue-50 rounded p-1">
                        <span className="text-xs text-blue-800">Wed: Bio Release</span>
                        <Icon name="Clock" size={12} className="text-blue-600" />
                      </div>
                      <div className="flex items-center justify-between bg-yellow-50 rounded p-1">
                        <span className="text-xs text-yellow-800">Fri: Scouting</span>
                        <span className="text-xs text-yellow-600">Sarah</span>
                      </div>
                    </div>
                    <div className="mt-2 bg-purple-100 rounded p-1">
                      <div className="text-xs text-purple-800 font-medium">3 Tasks Remaining</div>
                    </div>
                  </div>
                  <p className="text-xs text-purple-800 font-medium mt-1">Automated IPM scheduling with team assignments</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">⭐ Treatment Planner & Task Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">Calendar-based IPM scheduling with automated task assignment and team collaboration for commercial operations.</p>
            </button>

            {/* IPM Treatment Builder Example */}
            <button
              onClick={() => navigate('/premium-calculator-suite')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-400/10 to-cyan-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock IPM Builder Interface */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm">
                    <div className="text-xs font-medium text-gray-800 mb-2 flex items-center justify-center space-x-1">
                      <Icon name="Settings" size={14} className="text-teal-600" />
                      <span>Custom IPM Plan</span>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-teal-50 rounded p-2">
                        <div className="text-xs font-medium text-teal-800 mb-1">Week 1: Prevention</div>
                        <div className="text-xs text-teal-700">Regalia + Cucumeris Release</div>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <div className="text-xs font-medium text-blue-800 mb-1">Week 2: Monitoring</div>
                        <div className="text-xs text-blue-700">Cease + Scouting Protocol</div>
                      </div>
                      <div className="bg-green-50 rounded p-2">
                        <div className="text-xs font-medium text-green-800 mb-1">Week 3: Response</div>
                        <div className="text-xs text-green-700">Targeted Bio Release</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-teal-800 font-medium mt-1">AI creates personalized weekly treatment rotations</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">⭐ Custom IPM Treatment Builder</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">AI creates personalized weekly treatment rotations based on your crop type, pest pressure, and environmental conditions.</p>
            </button>

            {/* Pest Identification Guide Example */}
            <button
              onClick={() => navigate('/ipm-learning-center')}
              className="bg-white rounded-lg p-5 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left w-full flex flex-col"
            >
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg h-44 sm:h-48 flex flex-col items-center justify-center mb-3 sm:mb-4 p-3 sm:p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-red-400/10 to-orange-400/10"></div>
                <div className="relative text-center w-full">
                  {/* Mock Pest Guide Interface */}
                  <div className="bg-white/90 rounded-lg p-2 sm:p-3 shadow-sm">
                    <div className="text-xs font-medium text-gray-800 mb-2 flex items-center justify-center space-x-1">
                      <Icon name="BookOpen" size={14} className="text-red-600" />
                      <span>IPM Learning Center</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 bg-red-50 rounded p-2">
                        <div className="w-4 h-4 bg-red-200 rounded flex items-center justify-center">
                          <Icon name="Bug" size={10} className="text-red-600" />
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-medium text-red-800">Spider Mites</div>
                          <div className="text-xs text-red-600">Chapter 4 - Identification</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-orange-50 rounded p-2">
                        <div className="w-4 h-4 bg-orange-200 rounded flex items-center justify-center">
                          <Icon name="Leaf" size={10} className="text-orange-600" />
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-medium text-orange-800">Thrips Control</div>
                          <div className="text-xs text-orange-600">Chapter 6 - Biological Methods</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 bg-gray-100 rounded p-1">
                      <div className="text-xs text-gray-700 font-medium">9 Chapters Available</div>
                    </div>
                  </div>
                  <p className="text-xs text-red-800 font-medium mt-1">Comprehensive IPM education from expert content</p>
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight break-words">IPM Learning Center</h3>
              <p className="text-gray-600 text-sm leading-relaxed break-words">Expert agricultural content covering pest identification, biological controls, and treatment strategies from real IPM experience.</p>
            </button>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-600 mb-4 px-4">Ready to see these professional IPM tools in action?</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Button
                onClick={() => navigate('/ipm-operations-overview')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3"
              >
                Try Free Version
              </Button>
              <Button
                onClick={() => navigate('/premium-calculator-suite')}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50 px-6 py-3"
              >
                Start Premium Trial
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Who It's For Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <Icon name="Users" size={24} className="text-green-600 sm:w-8 sm:h-8" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Choose Your Growing Style</h2>
            </div>
            <p className="text-base sm:text-lg text-gray-600 px-4">Select the plan that fits your agricultural needs</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12 px-4 sm:px-0">
            {[
              'Commercial growers',
              'Greenhouse operators', 
              'Indoor cultivators',
              'Home gardeners',
              'Agriculture students',
              'Cannabis cultivators'
            ]?.map((user, index) => (
              <button
                key={index}
                onClick={() => navigate('/team-performance-hub')}
                className="flex items-center space-x-3 bg-green-50 rounded-lg p-3 sm:p-4 hover:bg-green-100 transition-colors"
              >
                <Icon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium text-sm sm:text-base">{user}</span>
              </button>
            ))}
          </div>

          <div className="text-center bg-green-50 rounded-lg p-4 sm:p-8 mx-4 sm:mx-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              AI-Powered Agricultural Companion for ANY Crop:
            </h3>
            <p className="text-gray-700 text-base sm:text-lg mb-3 sm:mb-4">
              Vegetables • Fruits • Cannabis • Greenhouse Products • Ornamentals
            </p>
            <Button
              onClick={() => navigate('/product-browser')}
              className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
            >
              Explore Product Database
            </Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <button 
                onClick={() => navigate('/')}
                className="flex items-center space-x-3 mb-4 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                  alt="GrowGuardian logo"
                  className="w-8 h-8 object-contain"
                />
                <h3 className="text-lg font-bold">GrowGuardian</h3>
              </button>
              <p className="text-gray-400 mb-4">
                AI-powered agricultural companion for vegetables, fruits, and cannabis crops
              </p>
              <p className="text-gray-400 text-sm">
                GrowGuardian LLC – Colorado
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white transition-colors text-left">Features</button></li>
                <li><button onClick={() => scrollToSection('premium')} className="text-gray-400 hover:text-white transition-colors text-left">Premium</button></li>
                <li><button onClick={() => navigate('/pricing-page')} className="text-gray-400 hover:text-white transition-colors text-left">Pricing</button></li>
                <li><button onClick={() => navigate('/application-logs')} className="text-gray-400 hover:text-white transition-colors text-left">Application Logs</button></li>
                <li><button onClick={() => navigate('/product-browser')} className="text-gray-400 hover:text-white transition-colors text-left">Product Browser</button></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="mailto:support@growguardian.work" className="text-gray-400 hover:text-white transition-colors">support@growguardian.work</a></li>
                <li><button onClick={() => navigate('/pricing-page#faq')} className="text-gray-400 hover:text-white transition-colors text-left">FAQ</button></li>
                <li><button onClick={() => navigate('/about-us-page')} className="text-gray-400 hover:text-white transition-colors text-left">About Us</button></li>
                <li><button onClick={() => navigate('/privacy-policy')} className="text-gray-400 hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms-conditions')} className="text-gray-400 hover:text-white transition-colors text-left">Terms & Conditions</button></li>
              </ul>
            </div>

            {/* Analytics */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Analytics Dashboard</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/ipm-operations-overview')} className="text-gray-400 hover:text-white transition-colors text-left">Operations Overview</button></li>
                <li><button onClick={() => navigate('/treatment-analytics')} className="text-gray-400 hover:text-white transition-colors text-left">Treatment Analytics</button></li>
                <li><button onClick={() => navigate('/team-performance-hub')} className="text-gray-400 hover:text-white transition-colors text-left">Team Performance</button></li>
                <li><button onClick={() => navigate('/compliance-dashboard')} className="text-gray-400 hover:text-white transition-colors text-left">Compliance Dashboard</button></li>
                <li><button onClick={() => navigate('/predictive-analytics-dashboard')} className="text-gray-400 hover:text-white transition-colors text-left">AI Predictive Analytics</button></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                <span>Website: </span>
                <a href="https://growguardian.work" className="text-green-400 hover:text-green-300 transition-colors" target="_blank" rel="noopener noreferrer">
                  GrowGuardian.work
                </a>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <button onClick={() => navigate('/pricing-page#terms')} className="text-gray-400 hover:text-white transition-colors">Terms & Privacy</button>
                <button onClick={() => navigate('/pricing-page#faq')} className="text-gray-400 hover:text-white transition-colors">FAQ</button>
                <a href="mailto:support@growguardian.work" className="text-gray-400 hover:text-white transition-colors">App Support</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;